import { useCallback, useState, type ClipboardEvent } from "react";
import { useUploadAssistantAttachmentMutation } from "@/partner/entities/assistant";
import { validatePhoto } from "./validatePhoto";
import type { FeedAttachment } from "./types";

export const MAX_LENGTH = 2000;
export const MAX_PHOTOS = 4;

export type ComposerErrorKey =
  | "tooLong"
  | "tooManyPhotos"
  | "photoTooLarge"
  | "photoType"
  | "upload";

export const COMPOSER_ERROR_KEYS: Record<ComposerErrorKey, string> = {
  tooLong: "partnerAssistant.errors.tooLong",
  tooManyPhotos: "partnerAssistant.errors.tooManyPhotos",
  photoTooLarge: "partnerAssistant.errors.photoTooLarge",
  photoType: "partnerAssistant.errors.photoType",
  upload: "partnerAssistant.errors.upload",
};

interface Options {
  conversationId: string | null;
  busy: boolean;
  onSend: (text: string, attachments: FeedAttachment[]) => void;
}

/**
 * Черновик сообщения помощника: текст до 2000 символов и до 4 фото.
 * Состояние живёт выше поля ввода: черновик и идущие загрузки переживают
 * закрытие панели и листа.
 */
export function useAssistantComposer({
  conversationId,
  busy,
  onSend,
}: Options) {
  const [text, setText] = useState("");
  const [photos, setPhotos] = useState<FeedAttachment[]>([]);
  const [uploading, setUploading] = useState(0);
  const [error, setError] = useState<ComposerErrorKey | null>(null);
  const [upload] = useUploadAssistantAttachmentMutation();

  const submit = () => {
    const value = text.trim();
    if (busy || uploading > 0 || (!value && photos.length === 0)) return;
    if (value.length > MAX_LENGTH) {
      setError("tooLong");
      return;
    }
    onSend(value, photos);
    setText("");
    setPhotos([]);
    setError(null);
  };

  const addFiles = async (files: FileList | File[] | null) => {
    const list = Array.from(files ?? []);
    if (list.length === 0) return;
    if (photos.length + uploading + list.length > MAX_PHOTOS) {
      setError("tooManyPhotos");
      return;
    }
    for (const file of list) {
      const problem = validatePhoto(file);
      if (problem) {
        setError(problem);
        return;
      }
    }
    setError(null);
    for (const file of list) {
      setUploading((n) => n + 1);
      try {
        const res = await upload({ variables: { file, conversationId } });
        const a = res.data?.uploadAssistantAttachment;
        if (a) setPhotos((prev) => [...prev, { id: a.id, url: a.url }]);
        else setError("upload");
      } catch {
        setError("upload");
      } finally {
        setUploading((n) => n - 1);
      }
    }
  };

  /** Сценарий «вот афиша»: фото вставляют из буфера, текст вставляется как обычно. */
  const pasteFiles = (e: ClipboardEvent) => {
    if (e.clipboardData.files.length === 0) return;
    e.preventDefault();
    void addFiles(e.clipboardData.files);
  };

  const removePhoto = useCallback(
    (id: string) => setPhotos((prev) => prev.filter((x) => x.id !== id)),
    []
  );

  return {
    text,
    setText,
    photos,
    removePhoto,
    uploading,
    error,
    submit,
    addFiles,
    pasteFiles,
    canAttach: photos.length + uploading < MAX_PHOTOS,
  };
}

export type AssistantComposerState = ReturnType<typeof useAssistantComposer>;
